'use client';

import React, { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import HLS from 'hls.js';
import Hls, { BufferFlushedData, BufferFlushingData, Events } from 'hls.js';
import { UserVideo } from "@/lib/types";
import { isHLSProvider, MediaLoadingStrategy, MediaPlayer, MediaPlayerInstance, MediaProvider, MediaProviderAdapter, MediaProviderChangeEvent, Poster, useMediaState } from '@vidstack/react';
import { PlayerStyle } from "@/components/player/PlayerStyle";
import { VideoLayout } from "@/components/player/layouts/video-layout";
import { TextTracks } from "@/components/player/TextTracks";
import { calculatePlayerSize, PlayerLayoutContextProvider } from "@/components/player/layouts/PlayerLayoutContext";
import { PlayerLanguageContextProvider } from "@/components/player/language/PlayerLanguageContext";
// @ts-ignore
import mux from 'mux-embed';
import { currentUser } from "@/firebase/firebaseAuth";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import PlayerError from "@/components/player/error/PlayerError";
import { useCaptionEditorContext } from "@/components/edit/captions/CaptionEditorContext";
interface PlayerConfig {
  video: UserVideo;
  className?: string;
  style?: CSSProperties;
  playerRefresh?: number;
  subtitleRefresh?: number;
  loadingStrategy?: MediaLoadingStrategy;
}
function PlayerInternal({
  video,
  className,
  style,
  playerRefresh = 0,
  subtitleRefresh = 0,
  loadingStrategy
}: PlayerConfig) {
  const playerRef = useRef<MediaPlayerInstance>(null);
  const width = useMediaState('width', playerRef);
  const [bufferState] = useState({
    waitingForBufferFlush: false,
    playBackRate: 1
  });
  const {
    selectedTimestamp
  } = useCaptionEditorContext();
  const onBufferFlushing = (e: Events.BUFFER_FLUSHING, data: BufferFlushingData) => {
    console.log('onBufferFlushing', data);
    // Only wait for flush if this is the main buffer
    if (playerRef.current && data.startOffset <= playerRef.current.currentTime && data.endOffset >= playerRef.current.currentTime) {
      console.log('onBufferFlushing - setting playback rate to 0');
      bufferState.waitingForBufferFlush = true;
      bufferState.playBackRate = playerRef.current.playbackRate;
      // This causes the video to show a buffering state until flushing is finished
      playerRef.current.playbackRate = 0;
    } else {
      console.log('onBufferFlushing - but not stopping playback as this is not the main buffer');
    }
  };
  const onBufferFlushed = (e: Events.BUFFER_FLUSHED, data: BufferFlushedData) => {
    console.log('onBufferFlushed', data, bufferState.waitingForBufferFlush);
    if (playerRef.current && bufferState.waitingForBufferFlush) {
      console.log('onBufferFlushed - setting playback to previous rate');
      playerRef.current.playbackRate = bufferState.playBackRate;
      playerRef.current.currentTime -= 0.001;
      bufferState.waitingForBufferFlush = false;
    }
  };
  const onProviderChange = (provider: MediaProviderAdapter | null, nativeEvent: MediaProviderChangeEvent) => {
    console.log('Provider changed', provider, nativeEvent);
    if (isHLSProvider(provider)) {
      // Static import
      provider.library = HLS;
      // console.log('HLS provider library', provider.library)

      provider.config = {
        // "debug": true,
        "backBufferLength": 90,
        "enableWorker": true,
        "lowLatencyMode": true
      };
      if (provider?.video?.canPlayType && provider.video.canPlayType('application/vnd.apple.mpegurl')) {
        // This will run in safari, where HLS is supported natively
        provider.video.src = video.url;
      } else if (HLS.isSupported()) {
        // This will run in all other modern browsers
        const hls = new Hls();
        hls.loadSource(video.url);
        hls.attachMedia(provider.video);
        mux.monitor(provider.video, {
          debug: false,
          hlsjs: hls,
          Hls,
          data: {
            env_key: process.env.NEXT_PUBLIC_MUX_ENV_KEY,
            player_name: 'Vidstack',
            player_init_time: mux.utils.now(),
            video_id: video.id,
            video_title: video.title,
            video_duration: video.duration
          }
        });
      }
    }
  };
  const onProviderSetup = (provider: MediaProviderAdapter) => {
    if (isHLSProvider(provider)) {
      // console.log('Provider instance', provider.instance)
      if (provider.instance) {
        // console.log('Adding event listeners')
        provider.instance.on(Events.BUFFER_FLUSHING, onBufferFlushing);
        provider.instance.on(Events.BUFFER_FLUSHED, onBufferFlushed);
      }
    }
  };
  const playerSize = calculatePlayerSize(width);
  const aspectRatio = useMemo(() => {
    return `${video?.aspectRatio || '16/9'}`;
  }, [video?.aspectRatio]);
  const playerStyle = useMemo(() => {
    return {
      ...style,
      aspectRatio,
      overflow: 'hidden'
    };
  }, [style, aspectRatio]);
  const playerStyleClassName = useMemo(() => {
    let classNames = "";
    console.log('Player size =', playerSize);
    if (video?.branding?.roundedCorners) {
      if (playerSize === 'large' || playerSize === 'medium') {
        console.log('Using classname player-radius');
        classNames += "player-radius ";
      } else if (playerSize === 'small') {
        console.log('Using classname player-radius-small');
        classNames += "player-radius-small ";
      } else {
        console.log('Using classname player-radius-xs');
        classNames += "player-radius-xs ";
      }
    }
    if (className) {
      classNames += className;
    }
    return classNames;
  }, [video?.branding?.roundedCorners, playerSize, className]);
  console.log('Player Style ClassName: ', playerStyleClassName, playerSize);

  // Detect when playerRefresh value has changed compared to previous value
  // After a reload we want to maintain the following state elements: timecode, play state, volume, language, captions (on/off)
  // Refresh the player only after a set period of time where there have been no changes (10 seconds maybe)
  const [debouncedPlayerRefresh, setDebouncedPlayerRefresh] = useState(playerRefresh);
  const playerRefreshRef = useRef(playerRefresh);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement) {
      // Set the current time to the selected timestamp
      playerElement.currentTime = selectedTimestamp;
      console.log('Setting player current time to', selectedTimestamp);
    }
  }, [selectedTimestamp, video.id]);
  useEffect(() => {
    const playerElement = playerRef.current;
    if (playerElement) {
      // Play the video when it can play
      const handleCanPlay = () => {
        playerElement?.play();
        playerElement?.removeEventListener('canplay', handleCanPlay);
      };
      playerElement.addEventListener('canplay', handleCanPlay);
      return () => {
        playerElement.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [video.id]);
  useEffect(() => {
    // Skip debounce refresh if playerRefresh is 1 to avoid refresh when player first loads
    if (playerRefresh === 1) {
      console.log('Skipping debounce refresh because playerRefresh is 1');
      setDebouncedPlayerRefresh(playerRefresh);
      return;
    }
    if (playerRefresh !== playerRefreshRef.current) {
      console.log(`Detected change in playerRefresh: ${playerRefresh}`);
      playerRefreshRef.current = playerRefresh;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setDebouncedPlayerRefresh(playerRefresh);
        console.log('Player refresh debounced', playerRefresh);

        // Restore playback state
        const playerState = sessionStorage.getItem(`player-${video.id}-state`);
        if (playerState === 'playing') {
          playerRef.current?.play().catch(error => {
            console.error('Error playing video', error);
          });
        } else if (playerState === 'paused') {
          playerRef.current?.pause();
        }
      }, 10000);
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [video.id, playerRefresh]);
  const handlePlay = () => {
    sessionStorage.setItem(`player-${video.id}-state`, 'playing');
  };
  const handlePause = () => {
    sessionStorage.setItem(`player-${video.id}-state`, 'paused');
  };
  const getAutoPlayValue = () => {
    const playerState = sessionStorage.getItem(`player-${video.id}-state`);
    if (playerState === 'playing') {
      return true;
    } else if (playerState === 'paused') {
      return false;
    } else {
      return video?.branding?.autoPlay || false;
    }
  };
  return <div style={playerStyle} className={playerStyleClassName} data-sentry-component="PlayerInternal" data-sentry-source-file="Player.tsx">
            <PlayerLayoutContextProvider width={width} data-sentry-element="PlayerLayoutContextProvider" data-sentry-source-file="Player.tsx">
                <PlayerStyle video={video} data-sentry-element="PlayerStyle" data-sentry-source-file="Player.tsx" />
                <MediaPlayer title={video?.title} src={video?.url} aspectRatio={aspectRatio} ref={playerRef} onProviderChange={onProviderChange} onProviderSetup={onProviderSetup} playsInline={true} autoPlay={getAutoPlayValue()} muted={video?.branding?.autoMute || false} loop={video?.branding?.loopVideo || false} key={video.id + "-player-" + debouncedPlayerRefresh} load={loadingStrategy} storage={`player-${video.id}`} onPlay={handlePlay} onPause={handlePause} data-sentry-element="MediaPlayer" data-sentry-source-file="Player.tsx">
                    <PlayerLanguageContextProvider languageOrder={video.languageOrder || []} captionShowingDefault={video?.branding?.autoCaptions} videoId={video.id} autoSwitch={video?.branding?.languageAutoSwitch ?? true} data-sentry-element="PlayerLanguageContextProvider" data-sentry-source-file="Player.tsx">
                        <MediaProvider
          // A hack to deal with black bars at either top or bottom of YouTube videos
          className={video.provider == 'youtube' ? 'youtube-zoom' : ''} data-sentry-element="MediaProvider" data-sentry-source-file="Player.tsx">
                            <Poster alt={video?.title || ''} src={video?.posterUrl} className="vds-poster" data-sentry-element="Poster" data-sentry-source-file="Player.tsx" />
                            <TextTracks videoId={video.id} languageOrder={video.languageOrder} key={video.id + "-subtitle-" + subtitleRefresh} data-sentry-element="TextTracks" data-sentry-source-file="Player.tsx" />
                        </MediaProvider>
                        <VideoLayout logoUrl={video?.branding?.logoUrl} logoVisible={video?.branding?.displayLogo} titleVisible={video?.branding?.displayVideoTitle} showCenterPlayButton={video?.branding?.centrePlayButton} centrePlayButtonShape={video?.branding?.centrePlayButtonShape} displayFlags={video?.branding?.displayFlags} languageMenuLocation={video?.branding?.languageMenuLocation} data-sentry-element="VideoLayout" data-sentry-source-file="Player.tsx" />
                    </PlayerLanguageContextProvider>
                </MediaPlayer>
            </PlayerLayoutContextProvider>
        </div>;
}

/**
 * Placeholder for the player before it loads. Used by the Loading Strategy Wrapper
 * @param props
 * @constructor
 */
function PlayerLoadingPlaceholder(props: PlayerConfig) {
  const aspectRatio = useMemo(() => {
    return `${props.video?.aspectRatio || '16/9'}`;
  }, [props.video?.aspectRatio]);
  return <div style={{
    width: '100%',
    aspectRatio: aspectRatio
  }} data-sentry-component="PlayerLoadingPlaceholder" data-sentry-source-file="Player.tsx">
        </div>;
}

/*
 * Wrapper for determining the loading strategy before the player loads as it can't be changed after the player loads
 */
function PlayerLoadingStrategyWrapper(props: PlayerConfig) {
  const [mediaPlayerLoadingStrategy, setMediaPlayerLoadingStrategy] = useState<MediaLoadingStrategy | undefined>(undefined);
  console.log('loadingStrategy', props.loadingStrategy);
  useEffect(() => {
    if (props.video?.id) {
      sessionStorage.removeItem(`player-${props.video?.id}-state`);
      sessionStorage.removeItem(`selectedLanguage-${props.video?.id}`);
      sessionStorage.removeItem(`captionShowing-${props.video?.id}`);
      localStorage.removeItem(`player-${props.video?.id}`);
      localStorage.removeItem(`${props.video?.url}:0:0`);
      if (props.loadingStrategy) {
        setMediaPlayerLoadingStrategy(props.loadingStrategy);
      } else if (props.video?.branding?.autoPlay) {
        setMediaPlayerLoadingStrategy('visible');
      } else if (!props.video?.branding?.minimalEmbedView) {
        setMediaPlayerLoadingStrategy('visible');
      } else {
        setMediaPlayerLoadingStrategy('play');
      }
    }
  }, [props.video?.id]);

  // Don't render the Player until video and player loading strategy are available
  if (!(props?.video?.id && mediaPlayerLoadingStrategy)) {
    return <PlayerLoadingPlaceholder {...props} />;
  }
  return <PlayerInternal {...props} loadingStrategy={mediaPlayerLoadingStrategy} data-sentry-element="PlayerInternal" data-sentry-component="PlayerLoadingStrategyWrapper" data-sentry-source-file="Player.tsx" />;
}
export default function Player(props: PlayerConfig) {
  const uid = currentUser()?.uid || '';
  return <ErrorBoundary uid={uid} maxErrorCount={2} customErrorComponent={PlayerError} data-sentry-element="ErrorBoundary" data-sentry-component="Player" data-sentry-source-file="Player.tsx">
            <PlayerLoadingStrategyWrapper {...props} data-sentry-element="PlayerLoadingStrategyWrapper" data-sentry-source-file="Player.tsx" />
        </ErrorBoundary>;
}